// DownloadedPurchases.tsx
import React, { useDeferredValue, useMemo, useCallback } from "react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
  DropdownMenuItem, DropdownMenuCheckboxItem
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import {
  Calendar as CalendarIcon, SlidersHorizontal, MoreHorizontal,
  Hand, Filter, X, Info, CheckCircle, CircleAlert, RotateCw,
  ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight
} from "lucide-react";
import type { downloadedEInvoice } from "@/types/downloadedEInvoice";
import { useDownloadEInvoices } from "@/hooks/useDownloadEInvoices";
import { useAuth } from "@/hooks/useAuth";
import { useEInvoices } from "@/hooks/useEInvoices";
import { useQueryClient } from "@tanstack/react-query";
import { useRefreshCooldown } from "@/hooks/useCooldown";
import { GlEntry, useInsertGlEntries } from "@/hooks/useAiAccounting";
import type { LhdnSearchDocumentsParams } from "@/types/lhdnSearchDocumentsParams";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

const cn = (...classes: (string | boolean)[]) => classes.filter(Boolean).join(" ");

export default function DownloadedPurchases() {
  const [q, setQ] = React.useState("");
  const dq = useDeferredValue(q);
  const [status, setStatus] = React.useState<string>("all");
  const [invoiceTypeFilter, setInvoiceTypeFilter] = React.useState<string>("all");
  const [directionFilter, setDirectionFilter] = React.useState<string>("all");
  const [dateRange, setDateRange] = React.useState<{ from: Date | undefined; to: Date | undefined; }>({ from: undefined, to: undefined });
  const reasonRef = React.useRef<HTMLTextAreaElement | null>(null);
  const [reasonTouched, setReasonTouched] = React.useState(false);
  const wait = (ms: number) => new Promise((res) => setTimeout(res, ms));
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25);
  const [debitGlDrafts, setDebitGlDrafts] = React.useState<Record<string, string>>({});
  const [creditGlDrafts, setCreditGlDrafts] = React.useState<Record<string, string>>({});
  const [glTarget, setGlTarget] = React.useState<downloadedEInvoice | null>(null);
  const [isSavingGl, setIsSavingGl] = React.useState(false);
  const [glDialogOpen, setGlDialogOpen] = React.useState(false);
  const [batchGlOpen, setBatchGlOpen] = React.useState(false);
  const [isBatchSavingGl, setIsBatchSavingGl] = React.useState(false);
  const batchDebitGlInputRef = React.useRef<HTMLInputElement | null>(null);
  const batchCreditGlInputRef = React.useRef<HTMLInputElement | null>(null);
  const cooldown = useRefreshCooldown();

  const [visibleCols, setVisibleCols] = React.useState({
    internalId: true,
    typeName: true,
    buyerName: true,
    supplierName: true,
    status: true,
    documentStatusReason: true,
    dateTimeValidated: true,
    total: true,
    uuid: false,
    submissionUid: false,
    intermediaryName: false,
    intermediaryTin: false,
    intermediaryRob: false,
    invoiceDirection: true,
    debitGlCode: true,
    creditGlCode: true,
    actions: true
  });

  const [selected, setSelected] = React.useState<string[]>([]);

  const { data, isFetching, error, refreshDownloads, backfillDownloads, updateGlCode } = useDownloadEInvoices("Purchases");
  const [backfillDialogOpen, setBackfillDialogOpen] = React.useState(false);
  const [backfillStartDate, setBackfillStartDate] = React.useState("2024-01-01");
  const [isBackfilling, setIsBackfilling] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const { user } = useAuth();
  const { rejectEInvoice } = useEInvoices();
  const queryClient = useQueryClient();

  const [rejectOpen, setRejectOpen] = React.useState(false);
  const [rejectTarget, setRejectTarget] = React.useState<downloadedEInvoice | null>(null);
  const [isRejecting, setIsRejecting] = React.useState(false);

  const [batchRejectOpen, setBatchRejectOpen] = React.useState(false);
  const batchReasonRef = React.useRef<HTMLTextAreaElement | null>(null);
  const [batchReasonTouched, setBatchReasonTouched] = React.useState(false);

  const dtf = React.useMemo(
    () => new Intl.DateTimeFormat(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit"
    }),
    []
  );

  const { precision } = useDecimalPrecision();
  const myr = React.useMemo(
    () =>
      new Intl.NumberFormat("en-MY", {
        style: "currency",
        currency: "MYR",
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      }),
    [precision]
  );

  const documents: downloadedEInvoice[] = React.useMemo(
    () => data?.results ?? [],
    [data]
  );

  const rows = useMemo(
    () =>
      filterRows(documents, {
        q,
        status,
        typeName: invoiceTypeFilter,
        direction: directionFilter,
        dateRange,
      }),
    [documents, q, status, invoiceTypeFilter, directionFilter, dateRange]
  );

  type DisplayRow = downloadedEInvoice & {
    __dateValidated: string;
    __total: string;
    __hasReject: boolean;
  };

  const canRejectCb = useCallback((r: downloadedEInvoice) => {
    const isValid = r.status === "Valid";
    const notAlreadyRequested = !r.rejectRequestDateTime;
    let within3Days = true;
    if (r.dateTimeValidated) {
      const diff = Date.now() - new Date(r.dateTimeValidated).getTime();
      within3Days = diff <= 3 * 24 * 60 * 60 * 1000;
    }
    return isValid && notAlreadyRequested && within3Days;
  }, []);

  const handleBatchRejectSubmit = async () => {
    if (selected.length === 0 || !user) {
      setBatchRejectOpen(false);
      return;
    }

    const reason = batchReasonRef.current?.value.trim() ?? "";
    if (!reason) {
      if (!batchReasonTouched) batchReasonRef.current?.focus();
      return;
    }

    setIsRejecting(true);
    
    const skippedInvoices = rows
      .filter((r) => selected.includes(r.uuid))
      .filter((r) => !canRejectCb(r))
      .map((r) => r.internalId);

    try {
      const maybeMutation: any = rejectEInvoice;

      // Only reject rows that are still valid per canRejectCb
      const targets: downloadedEInvoice[] = rows
        .filter((r) => selected.includes(r.uuid) && canRejectCb(r));

      for (const r of targets) {
        try {
          toast({
            title: `Rejecting ${r.internalId}...`
          })
          const args = { uuid: r.uuid, user, reason };
          if (maybeMutation?.mutateAsync) {
            await maybeMutation.mutateAsync(args);
          } else {
            await rejectEInvoice(args);
          }

          toast({
            title: "Invoice rejected",
            description: `${r.internalId} was rejected successfully. Rejecting the next in 3 seconds...`,
          });
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          toast({
            title: "Failed to reject",
            description: `${r.internalId} — ${msg}`,
            variant: "destructive",
          });
        }

        // Wait 10 seconds between each rejection attempt
        await wait(10_000);
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["downloaded-e-invoices"] }),
      ]);

      setSelected([]);
      setBatchRejectOpen(false);
    } catch (err) {
      console.error("Batch reject failed:", err);
      toast({
        title: "Batch rejection failed",
        description: "An unexpected error occurred. Check console for details.",
        variant: "destructive",
      });
    } finally {
      setIsRejecting(false);
      toast({
        title: "Batch rejection completed",
        description: skippedInvoices.length > 0 ? `The following invoices were skipped as they cannot be rejected (either not valid or validated more than 3 days ago): ${skippedInvoices.join(", ")}` : undefined
      })
    }
  };

  const counts = useMemo(() => {
    let valid = 0, invalid = 0, cancelled = 0, rejected = 0;

    for (const r of rows) {
      if (r.status === "Valid") valid++;
      if (r.status === "Invalid") invalid++;
      if (r.status === "Cancelled") cancelled++;
      if (r.rejectRequestDateTime && String(r.rejectRequestDateTime).trim() !== "") rejected++;
    }

    return { valid, invalid, cancelled, rejected, totalAfterFilters: rows.length };
  }, [rows]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  const pageRows = rows.slice(start, end);

  const displayRows: DisplayRow[] = useMemo(() => {
    return pageRows.map((r) => ({
      ...r,
      __dateValidated: r.dateTimeValidated ? dtf.format(new Date(r.dateTimeValidated)) : "-",
      __total: r.total != null ? myr.format(r.total) : "-",
      __hasReject: !!r.rejectRequestDateTime,
    }));
  }, [pageRows, dtf, myr]);

  const selectableIds = React.useMemo(
    () => rows.map((r) => r.uuid),
    [rows]
  );

  {displayRows.map((row) => {
    return (
      <TableRow key={row.internalId}>
        ...
        {visibleCols.actions && (
          <TableCell>
            <RowActions
              onReject={() => openRejectFor(row)}
              canReject={canRejectCb(row)}
              onEditGl={() => openGlFor(row)}
            />
          </TableCell>
        )}
      </TableRow>
    );
  })}

  React.useEffect(() => {
      setSelected((prev) => prev.filter((id) => selectableIds.includes(id)));
    }, [selectableIds]);

    React.useEffect(() => {
    const clearPointer = () => {
      // Remove the problematic class if some global code adds it
      document.body.classList.remove("pointer-events-none");

      // Also clear inline style if set
      if (document.body.style.pointerEvents === "none") {
        document.body.style.pointerEvents = "";
      }
    };

    // Run once on mount
    clearPointer();

    // Observe body for class/style changes and fix them
    const observer = new MutationObserver(clearPointer);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    return () => observer.disconnect();
  }, []);

  const toggleAll = (checked: boolean) => {
    setSelected(checked ? selectableIds : []);
  };

  const toggleOne = (id: string, checked: boolean) => {
    setSelected(prev =>
      checked ? [...new Set([...prev, id])] : prev.filter(x => x !== id)
    );
  };

  const activeFilters = [
    status !== "all" && { type: "Status", value: status },
    invoiceTypeFilter !== "all" && { type: "Invoice Type", value: invoiceTypeFilter },
    directionFilter !== "all" && { type: "Direction", value: directionFilter },
    dateRange.from && { type: "From", value: format(dateRange.from, "PP") },
    dateRange.to && { type: "To", value: format(dateRange.to, "PP") },
  ].filter(Boolean) as { type: string; value: string }[];

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const params: LhdnSearchDocumentsParams = {
        ...(dateRange.from && { issueDateFrom: dateRange.from.toISOString() }),
        ...(dateRange.to && { issueDateTo: dateRange.to.toISOString() }),
      };
      await refreshDownloads(params);
    } catch (er){
      console.error(er);
    }
    finally {
      setIsRefreshing(false);
    }
  };

  const openRejectFor = (r: downloadedEInvoice) => {
    setRejectTarget(r);
    setRejectOpen(true);
  };

  const openGlFor = useCallback((r: downloadedEInvoice) => {
    setGlTarget(r);
    setGlDialogOpen(true);
    setDebitGlDrafts((prev) => ({
      ...prev,
      [r.uuid]: prev[r.uuid] ?? (r.debitGlCode ?? ""),
    }));
    setCreditGlDrafts((prev) => ({
      ...prev,
      [r.uuid]: prev[r.uuid] ?? (r.creditGlCode ?? ""),
    }));
  }, []);

  React.useEffect(() => {
    setPage(1);
  }, [dq, status, invoiceTypeFilter, directionFilter, dateRange]);

  React.useEffect(() => {
    if (page > totalPages) setPage(totalPages);
    if (page < 1) setPage(1);
  }, [page, totalPages]);
  
  const insertGlEntries = useInsertGlEntries();
  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold leading-tight">Downloaded E-Invoices ● Purchases</h1>
          <p className="text-sm text-muted-foreground">
            Files you've imported or downloaded from LHDN. Inspect, validate, or reject them here.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setBackfillDialogOpen(true)}
            disabled={isBackfilling}
          >
            {isBackfilling ? "Backfilling…" : "Backfill History"}
          </Button>
          <Button
            onClick={handleRefresh}
            disabled={isRefreshing || isFetching || cooldown > 0}
          >
            <RotateCw
              className={`mr-2 h-4 w-4 ${(isRefreshing || isFetching) ? "animate-spin" : ""}`}
            />

            {cooldown > 0
              ? `Wait ${cooldown}s…`
              : isRefreshing || isFetching
                ? "Refreshing..."
                : "Refresh Downloads"}
          </Button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/20 dark:border-red-900/50 dark:text-red-400">
          Failed to load data. Try refreshing again.
        </div>
      )}

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Invoices</CardTitle>
            <Info className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{documents.length}</div>
            <p className="text-xs text-muted-foreground">{counts.totalAfterFilters} after filters</p>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Valid</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {counts.valid} <span className="mt-2 text-xs text-muted-foreground"> | ({counts.valid - counts.rejected}) w/ rejected</span>
            </div>
            <p className="text-xs text-muted-foreground">Successfully validated</p>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Invalid</CardTitle>
            <CircleAlert className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {counts.invalid}
            </div>
            <p className="text-xs text-muted-foreground">Has validation issues</p>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cancelled</CardTitle>
            <CircleAlert className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {counts.cancelled}
            </div>
            <p className="text-xs text-muted-foreground">Cancelled after issue</p>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Rejected</CardTitle>
            <CircleAlert className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {counts.rejected}
            </div>
            <p className="text-xs text-muted-foreground">Rejected by users</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-1 items-center gap-4">
            <Input
              placeholder="Search document no, UUID, buyer, supplier"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="max-w-sm"
            />

            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Valid">Valid</SelectItem>
                <SelectItem value="Invalid">Invalid</SelectItem>
                <SelectItem value="Cancelled">Cancelled</SelectItem>
                <SelectItem value="Rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>

            <Select value={directionFilter} onValueChange={setDirectionFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Direction" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Directions</SelectItem>
                <SelectItem value="Sent">Sent</SelectItem>
                <SelectItem value="Received">Received</SelectItem>
                <SelectItem value="Unknown">Unknown</SelectItem>
              </SelectContent>
            </Select>

            <Select value={invoiceTypeFilter} onValueChange={setInvoiceTypeFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Invoice Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="Invoice">Invoice</SelectItem>
                <SelectItem value="Credit Note">Credit Note</SelectItem>
                <SelectItem value="Debit Note">Debit Note</SelectItem>
                <SelectItem value="Refund Note">Refund Note</SelectItem>
                <SelectItem value="Self-billed Invoice">Self-billed Invoice</SelectItem>
                <SelectItem value="Self-billed Credit Note">Self-billed Credit Note</SelectItem>
                <SelectItem value="Self-billed Debit Note">Self-billed Debit Note</SelectItem>
                <SelectItem value="Self-billed Refund Note">Self-billed Refund Note</SelectItem>
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-[240px] justify-start text-left font-normal",
                    !dateRange.from && !dateRange.to && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, "PP")} - {format(dateRange.to, "PP")}
                      </>
                    ) : (
                      format(dateRange.from, "PP")
                    )
                  ) : (
                    "Validated date range"
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => setDateRange({ from: range?.from, to: range?.to })}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Column visibility */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-4">
                <SlidersHorizontal className="mr-2 h-4 w-4" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {Object.entries(visibleCols).map(([key, value]) => (
                <DropdownMenuCheckboxItem
                  key={key}
                  className="capitalize"
                  checked={value}
                  onCheckedChange={(checked) =>
                    setVisibleCols((prev) => ({ ...prev, [key]: !!checked }))
                  }
                >
                  {toReadableHeader(key)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Batch actions */}
        <div className="h-9">
          {selected.length > 0 && (
            <div className="flex items-center gap-2">
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setBatchRejectOpen(true)}
                disabled={isRejecting}
              >
                {isRejecting
                  ? `Rejecting ${selected.length}...`
                  : `Reject selected (${selected.length})`}
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setBatchGlOpen(true)}
                disabled={isBatchSavingGl}
              >
                Update GL for selected ({selected.length})
              </Button>
            </div>
          )}
        </div>

        {/* Active filters */}
        {activeFilters.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {activeFilters.map((f) => (
              <Badge key={`${f.type}-${f.value}`} variant="secondary" className="flex items-center gap-1">
                {f.type}: {f.value}
                <X
                  className="h-3 w-3 cursor-pointer"
                  onClick={() => {
                    if (f.type === "Status") setStatus("all");
                    if (f.type === "Invoice Type") setInvoiceTypeFilter("all");
                    if (f.type === "Direction") setDirectionFilter("all");
                    if (f.type === "From" || f.type === "To") setDateRange({from: undefined, to: undefined});
                  }}
                />
              </Badge>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setQ("");
                setStatus("all");
                setInvoiceTypeFilter("all");
                setDirectionFilter("all");
                setDateRange({ from: undefined, to: undefined });
              }}
            >
              Clear all filters
            </Button>
          </div>
        )}
      </div>

      {/* Empty state */}
      {!isFetching && documents.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100">
            <Filter className="h-6 w-6 text-gray-400" />
          </div>
          <h3 className="mt-4 font-semibold">No results found</h3>
          <p className="mt-2 text-sm text-gray-500">Try adjusting your search or filters.</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => {
              setQ("");
              setStatus("all");
              setInvoiceTypeFilter("all");
              setDirectionFilter("all");
              setDateRange({from: undefined, to: undefined});
            }}
          >
            Clear filters
          </Button>
        </div>
      )}

      {/* Table */}
      {documents.length > 0 && (
        <div className="relative rounded-md border shadow-lg">
          {(isFetching || isRefreshing) && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-black/40 backdrop-blur-sm">
              <div className="flex items-center gap-2 text-sm">
                <RotateCw className="h-4 w-4 animate-spin" />
                Loading…
              </div>
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={selectableIds.length > 0 && selected.length === selectableIds.length}
                    onCheckedChange={(v) => toggleAll(Boolean(v))}
                    disabled={selectableIds.length === 0}
                  />

                </TableHead>
                {visibleCols.internalId && <TableHead>Document Number</TableHead>}
                {visibleCols.typeName && <TableHead>Invoice Type</TableHead>}
                {visibleCols.buyerName && <TableHead>Buyer</TableHead>}
                {visibleCols.supplierName && <TableHead>Supplier</TableHead>}
                {visibleCols.invoiceDirection && <TableHead>Direction</TableHead>}
                {visibleCols.status && <TableHead>Status</TableHead>}
                {visibleCols.documentStatusReason && <TableHead>Reason for Cancel/Reject</TableHead>}
                {visibleCols.dateTimeValidated && <TableHead>Date Time Validated</TableHead>}
                {visibleCols.total && <TableHead className="text-right">Total</TableHead>}
                {visibleCols.uuid && <TableHead>UUID</TableHead>}
                {visibleCols.submissionUid && <TableHead>Submission UID</TableHead>}
                {visibleCols.intermediaryName && <TableHead>Intermediary Name</TableHead>}
                {visibleCols.intermediaryTin && <TableHead>Intermediary TIN</TableHead>}
                {visibleCols.intermediaryRob && <TableHead>Intermediary ROB</TableHead>}
                {visibleCols.debitGlCode && <TableHead>Debit GL Code</TableHead>}
                {visibleCols.creditGlCode && <TableHead>Credit GL Code</TableHead>}
                {visibleCols.actions && <TableHead>Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayRows.map((row) => (
                <TableRow key={row.uuid}>
                  <TableCell>
                    <Checkbox
                      checked={selected.includes(row.uuid)}
                      onCheckedChange={(v) => toggleOne(row.uuid, Boolean(v))}
                      disabled={false}
                    />
                  </TableCell>
                  {visibleCols.internalId && (
                    <TableCell className="font-medium">{row.internalId}</TableCell>
                  )}
                  {visibleCols.typeName && <TableCell>{row.typeName ?? "-"}</TableCell>}
                  {visibleCols.buyerName && <TableCell>{row.buyerName ?? "-"}</TableCell>}
                  {visibleCols.supplierName && <TableCell>{row.supplierName ?? "-"}</TableCell>}
                  {visibleCols.invoiceDirection && (
                    <TableCell>{normalizeDirection((row as any).invoiceDirection)}</TableCell>
                  )}
                  {visibleCols.status && (
                    <TableCell className="space-x-2">
                      <span className={statusPill(row.status)}>{row.status ?? "-"}</span>
                      {row.__hasReject && <span className={rejectedPill(true)}>Rejected</span>}
                    </TableCell>
                  )}
                  {visibleCols.documentStatusReason && (
                    <TableCell className="max-w-[360px] truncate">{row.documentStatusReason ?? "-"}</TableCell>
                  )}
                  {visibleCols.dateTimeValidated && <TableCell>{row.__dateValidated}</TableCell>}
                  {visibleCols.total && <TableCell className="text-right">{row.__total}</TableCell>}
                  {visibleCols.uuid && (
                    <TableCell className="truncate max-w-[280px]">{row.uuid ?? "-"}</TableCell>
                  )}
                  {visibleCols.submissionUid && (
                    <TableCell className="truncate max-w-[220px]">{row.submissionUID ?? "-"}</TableCell>
                  )}
                  {visibleCols.intermediaryName && <TableCell>{row.intermediaryName ?? "-"}</TableCell>}
                  {visibleCols.intermediaryTin && <TableCell>{row.intermediaryTin ?? "-"}</TableCell>}
                  {visibleCols.intermediaryRob && <TableCell>{row.intermediaryRob ?? "-"}</TableCell>}
                  {visibleCols.debitGlCode && <TableCell>{row.debitGlCode ?? "-"}</TableCell>}
                  {visibleCols.creditGlCode && <TableCell>{row.creditGlCode ?? "-"}</TableCell>}
                  {visibleCols.actions && (
                    <TableCell>
                      <RowActions
                        onReject={() => openRejectFor(row)}
                        canReject={canRejectCb(row)}
                        onEditGl={() => openGlFor(row)}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {/* Pagination bar */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 p-3 border-t">
            <div className="text-sm text-muted-foreground">
              Showing <span className="font-medium">{rows.length === 0 ? 0 : start + 1}</span>–
              <span className="font-medium">{Math.min(end, rows.length)}</span> of{" "}
              <span className="font-medium">{rows.length}</span>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Rows per page</span>
                <Select value={String(pageSize)} onValueChange={(v) => {
                  setPageSize(Number(v));
                  setPage(1);
                }}>
                  <SelectTrigger className="w-[88px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" onClick={() => setPage(1)} disabled={page === 1}>
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="mx-2 text-sm">
                  Page <span className="font-medium">{page}</span> of <span className="font-medium">{totalPages}</span>
                </span>
                <Button variant="outline" size="icon" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => setPage(totalPages)} disabled={page === totalPages}>
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

        </div>
      )}

      {/* Reject dialog */}
      {rejectOpen && (
        <Dialog
          open={rejectOpen}
          onOpenChange={(open) => {
            setRejectOpen(open);
            if (!open) {
              setRejectTarget(null);
              setIsRejecting(false);
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject invoice?</DialogTitle>
              <DialogDescription>
                {rejectTarget ? (
                  <span>
                    You’re rejecting <strong>{rejectTarget.internalId}</strong>. Please provide a reason.
                  </span>
                ) : "Provide a reason for rejection."}
              </DialogDescription>
            </DialogHeader>

            <div className="mt-2">
              <textarea
                key={rejectTarget?.uuid}
                ref={reasonRef}
                className="w-full rounded-md border p-2 text-sm"
                rows={4}
                placeholder="Reason for rejection…"
                onBlur={() => {
                  setReasonTouched(true);
                }}
              />

              {rejectTarget?.documentStatusReason && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Existing note: {rejectTarget.documentStatusReason}
                </p>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setRejectOpen(false);
                  setRejectTarget(null);
                  setIsRejecting(false);
                }}
                disabled={isRejecting}
              >
                Cancel
              </Button>

              <Button
                onClick={async () => {
                  const reason = reasonRef.current?.value.trim() ?? "";
                  if (!reason) {
                    if (!reasonTouched) reasonRef.current?.focus();
                    return;
                  }
                  if (!rejectTarget || !user) return;
                  try {
                    setIsRejecting(true);
                    const args = { uuid: rejectTarget.uuid, user, reason };
                    const maybeMutation: any = rejectEInvoice;
                    if (maybeMutation?.mutateAsync) {
                      await maybeMutation.mutateAsync(args);
                    } else {
                      await rejectEInvoice(args);
                    }
                    setRejectOpen(false);
                    setRejectTarget(null);
                    await Promise.all([
                      queryClient.invalidateQueries({ queryKey: ["downloaded-e-invoices"] }),
                    ]);
                  } finally {
                    setIsRejecting(false);
                    document.body.style.pointerEvents = "";
                    document.body.classList.remove("pointer-events-none");
                  }
                }}
                disabled={isRejecting}
              >
                {isRejecting ? "Rejecting…" : "Reject"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {/* Batch Reject dialog */}
      {batchRejectOpen && (
        <Dialog
          open={batchRejectOpen}
          onOpenChange={(open) => {
            setBatchRejectOpen(open);
            if (!open) {
              setIsRejecting(false);
              setBatchReasonTouched(false);
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                Reject {selected.length} invoice{selected.length > 1 ? "s" : ""}
              </DialogTitle>
              <DialogDescription>
                Please provide a reason for rejecting the selected invoice
                {selected.length > 1 ? "s" : ""}.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-2">
              <textarea
                key={`batch-${selected.join(",")}`} // reset on selection change
                ref={batchReasonRef}
                className="w-full rounded-md border p-2 text-sm"
                rows={4}
                placeholder="Reason for rejection…"
                onBlur={() => setBatchReasonTouched(true)}
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Some selected rows may be unable to be rejected based on the invoices' issuing and receiving party.
              </p>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setBatchRejectOpen(false)}
                disabled={isRejecting}
              >
                Cancel
              </Button>
              <Button onClick={handleBatchRejectSubmit} disabled={isRejecting}>
                {isRejecting ? "Rejecting…" : "Reject selected"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {/* Update gl Code */}
      {glDialogOpen && glTarget && (
        <Dialog
          open={glDialogOpen}
          onOpenChange={(open) => {
            setGlDialogOpen(open);
            if (!open) {
              setGlTarget(null);
              setIsSavingGl(false);
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update GL Code</DialogTitle>
              <DialogDescription>
                {`Set a GL Code for ${glTarget.internalId}.`}
              </DialogDescription>
            </DialogHeader>

            <div className="mt-2">
              <p className="text-sm text-muted-foreground">
                Debit GL Code
              </p>
              <Input
                className="w-full"
                value={debitGlDrafts[glTarget.uuid] ?? glTarget.debitGlCode ?? ""}
                onChange={(e) =>
                  setDebitGlDrafts((prev) => ({
                    ...prev,
                    [glTarget.uuid]: e.target.value,
                  }))
                }
                placeholder="Enter Debit GL Code…"
              />
            </div>

            <div className="mt-2">
              <p className="text-sm text-muted-foreground">
                Credit GL Code
              </p>
              <Input
                className="w-full"
                value={creditGlDrafts[glTarget.uuid] ?? glTarget.creditGlCode ?? ""}
                onChange={(e) =>
                  setCreditGlDrafts((prev) => ({
                    ...prev,
                    [glTarget.uuid]: e.target.value,
                  }))
                }
                placeholder="Enter Credit GL Code…"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setGlDialogOpen(false)}
                disabled={isSavingGl}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  const newDebitGl = (debitGlDrafts[glTarget.uuid] ?? glTarget.debitGlCode ?? "")
                    .trim();
                  const newCreditGl = (creditGlDrafts[glTarget.uuid] ?? glTarget.creditGlCode ?? "")
                    .trim();

                  const filledCount = [
                    newDebitGl,
                    newCreditGl
                  ].filter(Boolean).length;

                  const isValidSingleGl =
                    filledCount === 0 || filledCount === 3;

                  if (!isValidSingleGl) {
                    toast({
                      title: "Form incomplete",
                      description: "Please fill in all GL codes or leave all of them empty."
                    });
                    return;
                  }

                  try {
                    setIsSavingGl(true);
                    await updateGlCode.mutateAsync({
                      uuid: glTarget.uuid,
                      debitGlCode: newDebitGl === "" ? null : newDebitGl,
                      creditGlCode: newCreditGl === "" ? null : newCreditGl
                    });
                    queryClient.setQueryData(
                      ["downloaded-e-invoices"],
                      (old: any) => {
                        if (!old) return old;

                        return {
                          ...old,
                          results: old.results.map((r: downloadedEInvoice) =>
                            r.uuid === glTarget.uuid
                              ? {
                                  ...r,
                                  debitGlCode: newDebitGl || null,
                                  creditGlCode: newCreditGl || null
                                }
                              : r
                          ),
                        };
                      }
                    );
                    toast({
                      title: "GL Code updated",
                      description: `GL Code for ${glTarget.internalId} has been updated.`,
                    });
                    setGlDialogOpen(false);
                  } catch (err: any) {
                    toast({
                      title: "Failed to update GL Code",
                      description: err?.message ?? "Unexpected error",
                      variant: "destructive",
                    });
                  } finally {
                    queryClient.invalidateQueries({ queryKey: ["downloaded-e-invoices"] });
                    setIsSavingGl(false);
                    const issuedDate = new Date(glTarget.dateTimeIssued!);
                    const glEntries: GlEntry[] = [
                      {
                        account_code: newDebitGl,
                        transaction_date: issuedDate.toISOString().slice(0, 10), // YYYY-MM-DD
                        period_year: issuedDate.getFullYear(),
                        period_month: issuedDate.getMonth() + 1,
                        reference: glTarget.internalId,
                        amount: glTarget.total!,
                      },
                      {
                        account_code: newCreditGl,
                        transaction_date: issuedDate.toISOString().slice(0, 10), // YYYY-MM-DD
                        period_year: issuedDate.getFullYear(),
                        period_month: issuedDate.getMonth() + 1,
                        reference: glTarget.internalId,
                        amount: -glTarget.total!,
                      }
                    ];
                    try {
                      insertGlEntries.mutateAsync({ glEntries });
                      toast({
                          title: "GL Entries inserted",
                          description: `Inserted GL entries for ${glTarget.internalId} into the accounting system.`,
                        });
                      } catch (err: any) {
                        toast({
                          title: "Failed to insert GL entries",
                          description: err?.message ?? "Unexpected error",
                          variant: "destructive",
                        });
                      } finally {
                        toast({
                          title: "Insert GL Entries completed",
                          description: `GL Code update process for ${glTarget.internalId} is completed.`,
                        });
                      }
                  }
                }}
                disabled={isSavingGl}
              >
                {isSavingGl ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {/* Backfill History dialog */}
      {backfillDialogOpen && (
        <Dialog open={backfillDialogOpen} onOpenChange={setBackfillDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Backfill Historical Documents</DialogTitle>
              <DialogDescription>
                Fetches all documents from LHDN from the chosen start date to today, in 30-day
                windows. This runs in the background — click{" "}
                <strong>Refresh Downloads</strong> after a minute to see newly fetched documents.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-2 space-y-2">
              <p className="text-sm text-muted-foreground">Start date</p>
              <input
                type="date"
                className="w-full rounded-md border px-3 py-2 text-sm"
                value={backfillStartDate}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setBackfillStartDate(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Each 30-day window takes ~12 seconds due to LHDN rate limits. Starting from{" "}
                {backfillStartDate} will process approximately{" "}
                {Math.ceil(
                  (Date.now() - new Date(backfillStartDate).getTime()) / (30 * 86400_000),
                )}{" "}
                window(s).
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBackfillDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (!backfillStartDate) return;
                  setIsBackfilling(true);
                  setBackfillDialogOpen(false);
                  try {
                    await backfillDownloads({ startDate: backfillStartDate });
                    toast({
                      title: "Backfill started",
                      description:
                        "Documents are being fetched in the background. Click Refresh Downloads in a minute to see them.",
                    });
                  } catch (err: any) {
                    toast({
                      title: "Backfill failed to start",
                      description: err?.response?.data?.error ?? err?.message ?? "Unexpected error",
                      variant: "destructive",
                    });
                  } finally {
                    setIsBackfilling(false);
                  }
                }}
                disabled={!backfillStartDate}
              >
                Start Backfill
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {/* Batch update GL */}
      {batchGlOpen && (
        <Dialog
          open={batchGlOpen}
          onOpenChange={(open) => {
            setBatchGlOpen(open);
            if (!open) {
              setIsBatchSavingGl(false);
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                Update GL Code for {selected.length} invoice
                {selected.length > 1 ? "s" : ""}
              </DialogTitle>
              <DialogDescription>
                This will set the same GL Code on all selected invoices.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-2">
              <p className="text-sm text-muted-foreground">
                Debit GL Code
              </p>
              <Input
                ref={batchDebitGlInputRef}
                className="w-full"
                placeholder="Enter Debit GL Code to apply to all selected…"
              />
            </div>

            <div className="mt-2">
              <p className="text-sm text-muted-foreground">
                Credit GL Code
              </p>
              <Input
                ref={batchCreditGlInputRef}
                className="w-full"
                placeholder="Enter Credit GL Code to apply to all selected…"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setBatchGlOpen(false)}
                disabled={isBatchSavingGl}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  const areBatchGlCodesValid = () => {
                    const debit = batchDebitGlInputRef.current?.value.trim() ?? "";
                    const credit = batchCreditGlInputRef.current?.value.trim() ?? "";
    
                    const filledCount = [debit, credit].filter(Boolean).length;
    
                    // valid only if all 3 filled OR all 3 empty
                    return filledCount === 0 || filledCount === 3;
                  };

                  if (!areBatchGlCodesValid()) {
                    toast({
                      title: "Form incomplete",
                      description: "Please fill in all GL codes or leave all of them empty."
                    });
                    return;
                  }

                  const debitCode = batchDebitGlInputRef.current?.value.trim() ?? "";
                  const creditCode = batchCreditGlInputRef.current?.value.trim() ?? "";
                  const debitGlCode = debitCode === "" ? null : debitCode;
                  const creditGlCode = creditCode === "" ? null : creditCode;

                  setIsBatchSavingGl(true);

                    const targets = rows.filter((r) =>
                      selected.includes(r.uuid)
                    );

                  try {
                    for (const r of targets) {
                      await updateGlCode.mutateAsync({
                        uuid: r.uuid,
                        debitGlCode: debitGlCode,
                        creditGlCode: creditGlCode
                      });
                    }

                    toast({
                      title: "GL Codes updated",
                      description: `Updated GL Code for ${targets.length} invoice(s).`,
                    });
                    setBatchGlOpen(false);
                  } catch (err: any) {
                    toast({
                      title: "Batch GL update failed",
                      description: err?.message ?? "Unexpected error",
                      variant: "destructive",
                    });
                  } finally {
                    queryClient.invalidateQueries({ queryKey: ["downloaded-e-invoices"] });
                    setIsBatchSavingGl(false);
                    const glEntries: GlEntry[] = [];
                    for (const r of targets) {
                      const issuedDate = new Date(r.dateTimeIssued!);
                      const newEntry: GlEntry[] = [
                        {
                          account_code: r.debitGlCode!,
                          transaction_date: issuedDate.toISOString().slice(0, 10), // YYYY-MM-DD
                          period_year: issuedDate.getFullYear(),
                          period_month: issuedDate.getMonth() + 1,
                          reference: r.internalId,
                          amount: r.total!
                        },
                        {
                          account_code: r.creditGlCode!,
                          transaction_date: issuedDate.toISOString().slice(0, 10), // YYYY-MM-DD
                          period_year: issuedDate.getFullYear(),
                          period_month: issuedDate.getMonth() + 1,
                          reference: r.internalId,
                          amount: -r.total!
                        }
                      ]
                      glEntries.push(...newEntry);
                    }
                    try {
                      await insertGlEntries.mutateAsync({ glEntries });
                        toast({
                          title: "GL Entries inserted",
                          description: `Inserted GL entries for ${[...new Set(glEntries.map(e => e.reference))].join(", ")} into the accounting system.`,
                        });
                      } catch (err: any) {
                        toast({
                          title: "Failed to insert GL entries",
                          description: err?.message ?? "Unexpected error",
                          variant: "destructive",
                        });
                      } finally {
                        toast({
                          title: "Insert GL Entries completed",
                          description: `GL Code update process for ${[...new Set(glEntries.map(e => e.reference))].join(", ")} are completed.`,
                        });
                        queryClient.invalidateQueries({ queryKey: ["downloaded-e-invoices"] });
                      }
                  }
                }}
                disabled={isBatchSavingGl}
              >
                {isBatchSavingGl ? "Updating…" : "Update all"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const RowActions = React.memo(function RowActions({
  onReject,
  canReject,
  onEditGl,
}: {
  onReject: () => void;
  canReject: boolean;
  onEditGl: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            onEditGl();
          }}
        >
          <SlidersHorizontal className="mr-2 h-4 w-4" /> Update GL Code
        </DropdownMenuItem>

        <DropdownMenuItem
          disabled={!canReject}
          onSelect={(e) => {
            e.preventDefault();
            if (canReject) onReject();
          }}
        >
          <Hand className="mr-2 h-4 w-4" /> Reject
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

function statusPill(s: downloadedEInvoice["status"]) {
  const base = "rounded-full px-3 py-1 text-xs font-medium";
  switch (s) {
    case "Valid": return `${base} bg-green-100 text-green-800`;
    case "Invalid": return `${base} bg-red-100 text-red-800`;
    case "Cancelled": return `${base} bg-yellow-100 text-yellow-800`;
    case "Rejected": return `${base} bg-stone-200 text-stone-800`;
    default: return `${base} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
  }
}

function rejectedPill(hasRejection: boolean) {
  const base = "rounded-full px-3 py-1 text-xs font-medium";
  return hasRejection
    ? `${base} bg-red-100 text-red-800`
    : `${base} bg-green-100 text-green-800`;
}

function normalizeDirection(raw?: string | null): string {
    const v = raw?.trim();
    if (!v) return "Unknown";

    const lower = v.toLowerCase();
    if (lower === "sent") return "Sent";
    if (lower === "received") return "Received";
    return v; // fallback if backend sends other labels
}

function filterRows(
  rows: downloadedEInvoice[],
  opts: {
    q: string;
    status: string;
    typeName: string;
    direction: string;
    dateRange: { from?: Date; to?: Date };
  }
) {
  const { q, status, typeName: invoiceType, direction, dateRange } = opts;
  const ql = q.trim().toLowerCase();

  return rows.filter((r) => {
    const matchesQ =
      !ql ||
      (r.internalId ?? "").toLowerCase().includes(ql) ||
      (r.uuid ?? "").toLowerCase().includes(ql) ||
      (r.buyerName ?? "").toLowerCase().includes(ql) ||
      (r.supplierName ?? "").toLowerCase().includes(ql);

    const matchesStatus =
      status === "all"
        ? true
        : status === "Rejected"
        ? !!r.rejectRequestDateTime
        : r.status === status;

    const matchesType = invoiceType === "all" || r.typeName === invoiceType;

    const dir = normalizeDirection((r as any).invoiceDirection); // assuming field name is invoiceDirection
    const matchesDirection =
      direction === "all" ? true : dir === direction;

    const dt = r.dateTimeValidated ? new Date(r.dateTimeValidated) : null;
    const matchesDate =
      !dateRange.from && !dateRange.to
        ? true
        : !!dt &&
          (!dateRange.from || dt >= dateRange.from) &&
          (!dateRange.to || dt <= dateRange.to);

    return matchesQ && matchesStatus && matchesType && matchesDirection && matchesDate;
  });
}

function toReadableHeader(key: string) {
  const map: Record<string, string> = {
    internalId: "Document Number",
    typeName: "Invoice Type",
    buyerName: "Buyer",
    supplierName: "Supplier",
    status: "Status",
    documentStatusReason: "Reason for Cancel/Reject",
    dateTimeValidated: "Date Time Validated",
    total: "Total",
    uuid: "UUID",
    submissionUid: "Submission UID",
    intermediaryName: "Intermediary Name",
    intermediaryTin: "Intermediary TIN",
    intermediaryRob: "Intermediary ROB",
    invoiceDirection: "Direction",
    debitGlCode: "Debit GL Code",
    creditGlCode: "Credit GL Code",
    actions: "Actions",
  };
  return map[key] ?? key;
}
